/**
 * OTP1 — "reset this user's password to a one-time password", from the Users drawer (admin).
 *
 * Two steps in one modal. First a confirmation, because the reset is not a soft action: the user's
 * current password stops working and every sign-in they have ends at once (PATs survive unless the
 * box is ticked). Then the temporary password, shown ONCE: the gateway keeps only its hash, so this
 * component's state is the only place it exists. It is never put in a store, a URL, a log or the
 * clipboard without a click, and closing the dialog drops it.
 *
 * Same fallback as the device-link dialog (UX25): the password sits in a readonly field that selects
 * itself on focus, because a copy button is a promise a browser (http origin, no permission) may not
 * keep.
 *
 * EV1: when the gateway can send mail, «Отправить пароль пользователю на почту» is ticked by default
 * (the letter carries the password, its expiry and the login link). The password is still shown here
 * once — delivery is asynchronous, and the admin's copy is the fallback for a bounced letter — unless
 * «только письмо» is ticked too, in which case the response does not carry it at all.
 *
 * EV2: «Куда войдёт пользователь» picks the front the letter speaks for and links to (MCPGRAPH — the
 * gateway's own sign-in, Workspace, or «Мотор»). Only the id travels; the gateway builds the URL.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { OneTimePasswordReset, User } from '../api/schemas';
import { Modal } from './Modal';

export type MailApp = 'mcpgraph' | 'workspace' | 'motor';

/** EV2 — the fronts a letter can send the user to (the gateway resolves the id to a URL). */
export const MAIL_APPS: { id: MailApp; label: string }[] = [
  { id: 'mcpgraph', label: 'MCPGRAPH' },
  { id: 'workspace', label: 'Workspace' },
  { id: 'motor', label: 'Мотор' },
];

type Reset = (
  id: string,
  body: { revoke_tokens?: boolean; send_email?: boolean; email_only?: boolean; app?: MailApp },
) => Promise<OneTimePasswordReset>;

function reason(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 403) return 'Недостаточно прав: сбрасывать пароль может только администратор.';
    if (e.status === 404) return 'Пользователь не найден.';
    if (e.status === 429) return 'Слишком много сбросов подряд — попробуйте через минуту.';
    const detail = (e.body as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail) return detail;
    return e.message;
  }
  return String(e);
}

export function formatUntil(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function OneTimePasswordDialog({
  user,
  onClose,
  onDone,
  reset = (id, body) => api.resetOneTimePassword(id, body),
  mailAvailable = false,
}: {
  user: User;
  onClose: () => void;
  /** Called once the reset happened (the drawer reloads the user to show the pending expiry). */
  onDone?: (res: OneTimePasswordReset) => void;
  reset?: Reset;
  /** EV1 — the gateway's mailer is configured (`GET /auth/methods` → `email`). */
  mailAvailable?: boolean;
}) {
  const [revokeTokens, setRevokeTokens] = useState(false);
  const [sendEmail, setSendEmail] = useState(mailAvailable);
  const [emailOnly, setEmailOnly] = useState(false);
  const [app, setApp] = useState<MailApp>('mcpgraph');
  // The capability may arrive after the dialog opened; follow it until the admin acts.
  useEffect(() => setSendEmail(mailAvailable), [mailAvailable]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OneTimePasswordReset | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);

  async function confirm() {
    if (busy || result) return; // one deliberate click = one reset
    setBusy(true);
    setError(null);
    try {
      const res = await reset(user.id, {
        revoke_tokens: revokeTokens,
        // Without a mailer the fields are left out: the server's default is then "no letter".
        ...(mailAvailable ? { send_email: sendEmail, email_only: sendEmail && emailOnly } : {}),
        // EV2 — which front the letter links to; only meaningful when a letter is sent.
        ...(mailAvailable && sendEmail ? { app } : {}),
      });
      setResult(res);
      onDone?.(res);
    } catch (e) {
      setError(reason(e));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!result?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(result.temporary_password);
      setCopied(true);
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Не удалось скопировать — выделите пароль в поле и скопируйте вручную.');
    }
  }

  const close = () => {
    // Drop the secret before the dialog goes away.
    setResult(null);
    onClose();
  };

  if (result && result.temporary_password == null) {
    // EV1 — e-mail only: the letter is the password's one copy; there is nothing to show here.
    return (
      <Modal title="Одноразовый пароль" onClose={close} width={440}
        footer={<button className="btn btn-sm btn-primary" data-testid="otp-close" onClick={close}>Готово</button>}
      >
        <div data-testid="otp-result">
          <p className="muted" style={{ marginTop: 0 }} data-testid="otp-emailed">
            Одноразовый пароль отправлен на почту <b>{user.email}</b>; действует до{' '}
            {formatUntil(result.expires_at)}. При входе пользователь задаст новый пароль.
          </p>
          {(result.sessions_revoked > 0 || result.tokens_revoked > 0) && (
            <p className="setting-help" data-testid="otp-revoked" style={{ marginBottom: 0 }}>
              Завершено входов: {result.sessions_revoked}
              {result.tokens_revoked > 0 ? `; отозвано токенов: ${result.tokens_revoked}` : ''}.
            </p>
          )}
        </div>
      </Modal>
    );
  }

  if (result) {
    return (
      <Modal title="Одноразовый пароль" onClose={close} width={440}
        footer={<button className="btn btn-sm btn-primary" data-testid="otp-close" onClick={close}>Готово</button>}
      >
        <div data-testid="otp-result">
          <p className="muted" style={{ marginTop: 0 }}>
            {result.emailed ? (
              <span data-testid="otp-emailed">
                Письмо с паролем отправлено на <b>{user.email}</b>. Если оно не дойдёт — передайте пароль сами.
              </span>
            ) : (
              <>Передайте пароль пользователю <b>{user.email}</b>.</>
            )}
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              data-testid="otp-password"
              className="input mono"
              readOnly
              value={result.temporary_password ?? ''}
              aria-label="Одноразовый пароль (выделите, чтобы скопировать)"
              spellCheck={false}
              autoComplete="off"
              onFocus={(e) => e.currentTarget.select()}
              onClick={(e) => e.currentTarget.select()}
              style={{ flex: 1, fontSize: 14 }}
            />
            <button className="btn btn-sm" data-testid="otp-copy" onClick={() => void copy()}>
              {copied ? 'Скопировано' : 'Копировать'}
            </button>
          </div>
          <p className="setting-help" data-testid="otp-note" style={{ marginBottom: 0 }}>
            Пароль покажется один раз; действует до {formatUntil(result.expires_at)}; при входе
            пользователь задаст новый пароль.
          </p>
          {(result.sessions_revoked > 0 || result.tokens_revoked > 0) && (
            <p className="setting-help" data-testid="otp-revoked" style={{ marginBottom: 0 }}>
              Завершено входов: {result.sessions_revoked}
              {result.tokens_revoked > 0 ? `; отозвано токенов: ${result.tokens_revoked}` : ''}.
            </p>
          )}
          {error && <div className="form-error">{error}</div>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Сбросить пароль (одноразовый)" onClose={busy ? () => {} : close} width={440}
      footer={
        <>
          <button className="btn btn-sm" disabled={busy} onClick={close}>Отмена</button>
          <button className="btn btn-sm btn-danger" data-testid="otp-confirm" disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Сбрасываю…' : 'Сбросить пароль'}
          </button>
        </>
      }
    >
      <div data-testid="otp-confirm-step">
        <p className="confirm-msg" style={{ marginTop: 0 }}>
          Сбросить пароль <b>{user.email}</b> на одноразовый? Текущий пароль перестанет работать, а все
          входы пользователя будут завершены. При следующем входе с одноразовым паролем пользователь
          задаст новый.
        </p>
        <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            data-testid="otp-revoke-tokens"
            checked={revokeTokens}
            disabled={busy}
            onChange={(e) => setRevokeTokens(e.target.checked)}
          />
          Также отозвать токены доступа (PAT / MCP) — если учётная запись могла быть скомпрометирована
        </label>
        <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            data-testid="otp-send-email"
            checked={mailAvailable && sendEmail}
            disabled={busy || !mailAvailable}
            onChange={(e) => setSendEmail(e.target.checked)}
          />
          Отправить пароль пользователю на почту
          {!mailAvailable && <span className="muted"> (почта на шлюзе не настроена)</span>}
        </label>
        {mailAvailable && sendEmail && (
          <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 24 }}>
            <input
              type="checkbox"
              data-testid="otp-email-only"
              checked={emailOnly}
              disabled={busy}
              onChange={(e) => setEmailOnly(e.target.checked)}
            />
            Только письмо — не показывать пароль здесь
          </label>
        )}
        {mailAvailable && sendEmail && (
          <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 24 }}>
            Куда войдёт пользователь
            <select
              className="input"
              data-testid="otp-app"
              value={app}
              disabled={busy}
              onChange={(e) => setApp(e.target.value as MailApp)}
              style={{ width: 'auto' }}
            >
              {MAIL_APPS.map((a) => (
                <option key={a.id} value={a.id}>{a.label}{a.id === 'mcpgraph' ? ' (по умолчанию)' : ''}</option>
              ))}
            </select>
          </label>
        )}
        {error && <div className="form-error" data-testid="otp-error">{error}</div>}
      </div>
    </Modal>
  );
}
